/*
  FAST CART SYSTEM - Instant Updates
  © 2024 KondaSoft
*/

class Cart extends HTMLElement {
  constructor() {
    super()
    this.initializeEventListeners()
    this.showOffcanvasIfUrl()
  }

  initializeEventListeners() {
    // Simple event delegation for dynamic content
    this.addEventListener('click', (e) => {
      if (e.target.classList.contains('btn-remove-item')) {
        this.removeItem(e.target.dataset.lineItemKey)
      }
    })

    this.addEventListener('change', (e) => {
      if (e.target.name === 'updates[]') {
        this.updateQuantity(e.target.dataset.lineItemKey, e.target.value)
      }
    })
  }

  // FAST ADD TO CART - Instant feedback
  async add(formData) {
    const button = document.querySelector('.btn-add-to-cart')
    const originalText = button ? button.innerHTML : ''
    
    if (button) {
      button.innerHTML = 'Adding...'
      button.disabled = true
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      })

      const result = await response.json()

      if (response.ok) {
        // INSTANT feedback
        this.showSuccessMessage('Added to cart!')
        
        // Update cart count immediately
        this.updateCartCount(1)
        
        // Show cart drawer
        this.showCartDrawer()
        
        // Reload cart content in background
        setTimeout(() => {
          this.reloadCartContent()
        }, 100)
        
      } else {
        throw new Error(result.description || 'Failed to add item')
      }
    } catch (error) {
      console.error('Add to cart error:', error)
      this.showErrorMessage(error.message)
    } finally {
      if (button) {
        button.innerHTML = originalText
        button.disabled = false
      }
    }
  }

  // FAST REMOVE ITEM
  async removeItem(lineItemKey) {
    const button = document.querySelector(`[data-line-item-key="${lineItemKey}"]`)
    
    if (button) {
      button.innerHTML = '...'
      button.disabled = true
    }

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: lineItemKey,
          quantity: 0
        })
      })

      if (response.ok) {
        // Remove item from DOM immediately
        const cartItem = button.closest('.cart-item')
        if (cartItem) {
          cartItem.style.opacity = '0.5'
          cartItem.style.transform = 'translateX(-100%)'
          setTimeout(() => {
            cartItem.remove()
          }, 300)
        }
        
        // Update cart count
        this.updateCartCount(-1)
        
        // Show success message
        this.showSuccessMessage('Item removed')
        
        // Reload cart content
        setTimeout(() => {
          this.reloadCartContent()
        }, 500)
        
      } else {
        throw new Error('Failed to remove item')
      }
    } catch (error) {
      console.error('Remove item error:', error)
      this.showErrorMessage('Failed to remove item')
      
      if (button) {
        button.innerHTML = '✕'
        button.disabled = false
      }
    }
  }

  // FAST QUANTITY UPDATE
  async updateQuantity(lineItemKey, quantity) {
    const input = document.querySelector(`[data-line-item-key="${lineItemKey}"]`)
    
    if (quantity < 1) {
      this.removeItem(lineItemKey)
      return
    }

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: lineItemKey,
          quantity: parseInt(quantity)
        })
      })

      if (response.ok) {
        // Update price immediately (estimate)
        const priceElement = input.closest('.cart-item').querySelector('.cart-item-price')
        if (priceElement) {
          priceElement.style.opacity = '0.6'
        }
        
        // Reload cart content
        setTimeout(() => {
          this.reloadCartContent()
        }, 100)
        
      } else {
        throw new Error('Failed to update quantity')
      }
    } catch (error) {
      console.error('Update quantity error:', error)
      this.showErrorMessage('Failed to update quantity')
    }
  }

  // INSTANT CART COUNT UPDATE
  updateCartCount(change) {
    const badges = document.querySelectorAll('.cart-count-badge')
    badges.forEach(badge => {
      const currentCount = parseInt(badge.textContent || '0')
      const newCount = Math.max(0, currentCount + change)
      badge.textContent = newCount
      badge.style.display = newCount > 0 ? 'inline' : 'none'
    })
  }

  // FAST CART CONTENT RELOAD
  async reloadCartContent() {
    try {
      const response = await fetch('/cart?section_id=offcanvas-cart')
      const html = await response.text()
      
      const parser = new DOMParser()
      const doc = parser.parseFromString(html, 'text/html')
      const newContent = doc.querySelector('cart-container')
      
      if (newContent) {
        this.replaceWith(newContent)
      }
    } catch (error) {
      console.error('Reload cart error:', error)
    }
  }

  // SHOW CART DRAWER
  showCartDrawer() {
    const offcanvas = document.querySelector('#offcanvas-cart')
    if (offcanvas) {
      window.bootstrap.Offcanvas.getOrCreateInstance(offcanvas).show()
    }
  }

  // SUCCESS MESSAGE
  showSuccessMessage(message) {
    this.showToast(message, 'success')
  }

  // ERROR MESSAGE
  showErrorMessage(message) {
    this.showToast(message, 'error')
  }

  // TOAST NOTIFICATION
  showToast(message, type = 'info') {
    const toast = document.createElement('div')
    toast.className = `toast-notification toast-${type}`
    toast.innerHTML = `
      <div class="toast-content">
        <span class="toast-icon">${type === 'success' ? '✅' : '❌'}</span>
        <span class="toast-message">${message}</span>
      </div>
    `
    
    document.body.appendChild(toast)
    
    // Show animation
    setTimeout(() => {
      toast.classList.add('show')
    }, 100)
    
    // Remove after 3 seconds
    setTimeout(() => {
      toast.classList.remove('show')
      setTimeout(() => {
        if (toast.parentNode) {
          toast.remove()
        }
      }, 300)
    }, 3000)
  }

  showOffcanvasIfUrl() {
    if (new URLSearchParams(window.location.search).has('cart')) {
      const offcanvas = document.querySelector('#offcanvas-cart')
      if (offcanvas) {
        window.bootstrap.Offcanvas.getOrCreateInstance(offcanvas).show()
      }
    }
  }
}

customElements.define('cart-container', Cart)

// GLOBAL CART FUNCTIONS for easy access
window.addToCart = function(formData) {
  const cart = document.querySelector('cart-container')
  if (cart) {
    cart.add(formData)
  }
}

window.removeCartItem = function(lineItemKey) {
  const cart = document.querySelector('cart-container')
  if (cart) {
    cart.removeItem(lineItemKey)
  }
}

window.updateQuantity = function(lineItemKey, quantity) {
  const cart = document.querySelector('cart-container')
  if (cart) {
    cart.updateQuantity(lineItemKey, quantity)
  }
}

// FAST ADD TO CART FORM HANDLING
document.addEventListener('DOMContentLoaded', function() {
  const addToCartForms = document.querySelectorAll('form[action="/cart/add"]')
  
  addToCartForms.forEach(form => {
    form.addEventListener('submit', function(e) {
      e.preventDefault()
      const formData = new FormData(this)
      window.addToCart(formData)
    })
  })
})

// TOAST STYLES
const toastStyles = document.createElement('style')
toastStyles.textContent = `
  .toast-notification {
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 9999;
    padding: 12px 20px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
    transform: translateX(100%);
    transition: all 0.3s ease;
    min-width: 250px;
  }
  
  .toast-notification.show {
    transform: translateX(0);
  }
  
  .toast-success {
    background: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
  }
  
  .toast-error {
    background: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
  }
  
  .toast-content {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .toast-icon {
    font-size: 16px;
  }
  
  .toast-message {
    font-weight: 500;
  }
  
  .cart-item {
    transition: all 0.3s ease;
  }
`
document.head.appendChild(toastStyles)